import React, { useState, useEffect, useContext, useMemo } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import Select from 'react-select';
import { AuthContext } from '../../AuthContext';
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import "../../assets/CSS/Dashboard.css";

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050';
const IMAGE_BASE_URL = process.env.REACT_APP_API_BASE_PATH || BASE_URL;

export default function ManagerRemediation() {
  const { user } = useContext(AuthContext);
  const [tasks, setTasks] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSidebarVisible, setIsSidebarVisible] = useState(window.innerWidth >= 768);
  const [activeTab, setActiveTab] = useState('live');

  // Filters State
  const [searchQuery, setSearchQuery] = useState("");
  const [workerFilter, setWorkerFilter] = useState("");
  const [supervisorFilter, setSupervisorFilter] = useState("");

  // Remediation Modal State
  const [showModal, setShowModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [workersList, setWorkersList] = useState([]);
  const [workerSearch, setWorkerSearch] = useState('');
  const [assignSupervisor, setAssignSupervisor] = useState('');
  const [supervisorsList, setSupervisorsList] = useState([]);
  const [assignWorker, setAssignWorker] = useState('');
  const [assignQty, setAssignQty] = useState('');
  const [assignNotes, setAssignNotes] = useState('');
  const [standardTime, setStandardTime] = useState('');
  const [dueTime, setDueTime] = useState('');
  const [verifier, setVerifier] = useState('QC');
  const [assigning, setAssigning] = useState(false);
  const [submittingManpower, setSubmittingManpower] = useState(false);

  const toggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);

  useEffect(() => {
    if (!user) { window.location.href = "/"; return; }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/wip-dashboard/manager/overview`, {
        params: {
          role: user?.Auth,
          department: user?.Department,
          supervisorName: user?.Name   // ← used to filter tickets for this supervisor
        }
      });
      if (res.data.success) {
        setTasks(res.data.tasks);
        setTickets(res.data.escalated_tickets);
      }
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Failed to fetch manager data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const openRemediationModal = async (ticket) => {
    setSelectedTicket(ticket);
    setAssignSupervisor('');
    setAssignWorker('');
    setAssignQty('');
    setAssignNotes('');
    setWorkerSearch('');
    setWorkersList([]);
    setSupervisorsList([]);
    setShowModal(true);
    try {
      // Fetch supervisors list
      const supRes = await axios.get(`${BASE_URL}/api/wip-dashboard/manager/supervisors`);
      if (supRes.data.success) setSupervisorsList(supRes.data.supervisors);

      // Fetch workers from supervisor's department (user.Department)
      const res = await axios.get(`${BASE_URL}/api/wip-dashboard/manager/workers-list`, {
        params: { dept: user?.Department }
      });
      if (res.data.success) setWorkersList(res.data.workers);
    } catch (err) {
      console.error('Fetch error:', err);
      setWorkersList([]);
      setSupervisorsList([]);
    }
  };

  const availableQty = selectedTicket ? (parseInt(selectedTicket.shortage_qty) || (parseInt(selectedTicket.expected_qty || 0) - parseInt(selectedTicket.actual_qty || 0))) : 0;

  const handleAssignRemediation = async () => {
    if (!assignSupervisor || !assignWorker || !assignQty) {
      Swal.fire('Validation', 'Supervisor, Worker, aur quantity teeno required hain.', 'warning');
      return;
    }
    if (parseInt(assignQty) > availableQty) {
      Swal.fire('Quantity Error', `Sirf ${availableQty} pcs ki shortage hai. Isse zyada assign nahi kar sakte!`, 'error');
      return;
    }
    if (parseInt(assignQty) <= 0) {
      Swal.fire('Validation', 'Quantity 0 se zyada honi chahiye.', 'warning');
      return;
    }

    const workerObj = workersList.find(w => String(w.worker_id) === assignWorker);
    if (!workerObj) return;

    try {
      setAssigning(true);
      const res = await axios.post(`${BASE_URL}/api/wip-dashboard/manager/assign-remediation`, {
        ticket_id: selectedTicket.ticket_id,
        new_worker_id: assignWorker,
        new_worker_name: workerObj.worker_name,
        qty: parseInt(assignQty),
        assigned_by: user?.Name || user?.EmployeeID || 'MANAGER',
        notes: assignNotes,
        standard_time: standardTime,
        due_time: dueTime,
        verifier: verifier,
        responsible_supervisor: assignSupervisor
      });

      if (res.data.success) {
        setShowModal(false);
        Swal.fire({
          icon: 'success',
          title: 'Remediation Assigned!',
          html: `<b>${workerObj.worker_name}</b> ko <b>${assignQty} pcs</b> ka kaam assign ho gaya.<br/>Ticket <b>#${selectedTicket.ticket_id}</b> update ho gaya.`,
          confirmButtonColor: '#0f172a'
        });
        fetchData(); // Refresh data
      }
    } catch (err) {
      console.error(err);
      Swal.fire('Error', err.response?.data?.message || 'Assignment failed', 'error');
    } finally {
      setAssigning(false);
    }
  };

  const handleManpowerAction = async (actionType) => {
    // Basic logic to demonstrate the API
    Swal.fire({
      title: `${actionType} Confirmation`,
      html: `
        <div class="mb-3">
          <label>Process ID (Red Process):</label>
          <input type="number" id="mp-process" class="form-control" placeholder="e.g. 1" />
        </div>
        <div class="mb-3">
          <label>Worker ID:</label>
          <input type="text" id="mp-worker" class="form-control" placeholder="Enter Worker ID" />
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Submit Action',
      preConfirm: () => {
        const pId = document.getElementById('mp-process').value;
        const wId = document.getElementById('mp-worker').value;
        if (!pId || !wId) {
          Swal.showValidationMessage('Process ID and Worker ID are required');
        }
        return { process_id: pId, worker_id: wId };
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setSubmittingManpower(true);
          const res = await axios.post(`${BASE_URL}/api/wip-dashboard/manager/manpower/adjust`, {
            process_id: result.value.process_id,
            action_type: actionType,
            worker_id: result.value.worker_id,
            approved_by: user?.Name || 'Manager'
          }, { withCredentials: true });

          if (res.data.success) {
            Swal.fire('Success', res.data.message, 'success');
          }
        } catch (err) {
          Swal.fire('Error', err.response?.data?.message || 'Failed to submit action', 'error');
        } finally {
          setSubmittingManpower(false);
        }
      }
    });
  };

  const getTimeElapsed = (startTime) => {
    const start = new Date(startTime);
    const now = new Date();
    const diff = Math.floor((now - start) / 60000);
    if (diff < 60) return `${diff} mins ago`;
    const hours = Math.floor(diff / 60);
    const mins = diff % 60;
    return `${hours}h ${mins}m ago`;
  };

  const uniqueWorkers = useMemo(() => {
    const workers = new Map();
    tasks.forEach(t => {
      if (t.worker_id && !workers.has(t.worker_id))
        workers.set(t.worker_id, `${t.First_Name} ${t.Last_Name}`.trim());
    });
    return Array.from(workers.entries()).map(([id, name]) => ({ id, name }));
  }, [tasks]);

  const uniqueSupervisors = useMemo(() => {
    const s = new Set();
    tasks.forEach(t => { if (t.supervisor) s.add(t.supervisor); });
    return Array.from(s);
  }, [tasks]);

  const filteredTasks = useMemo(() => {
    return tasks.filter(t => {
      const q = searchQuery.toLowerCase();
      const matchesSearch = t.lot_id?.toLowerCase().includes(q) || t.process_name?.toLowerCase().includes(q) || t.First_Name?.toLowerCase().includes(q) || t.worker_id?.toLowerCase().includes(q);
      const matchesWorker = workerFilter ? t.worker_id === workerFilter : true;
      const matchesSupervisor = supervisorFilter ? t.supervisor === supervisorFilter : true;
      return matchesSearch && matchesWorker && matchesSupervisor;
    });
  }, [tasks, searchQuery, workerFilter, supervisorFilter]);

  return (
    <div className="d-flex dashboard">
      <div className={isSidebarVisible ? "sidebar-container shadow-sm" : "sidebar-hidden"}>
        <Sidebar />
      </div>

      <div className="flex-grow-1">
        <Header toggleSidebar={toggleSidebar} isSidebarVisible={isSidebarVisible} />

        <main className="main-container p-0 pb-5" style={{ background: "#f8fafc", fontFamily: "'Inter', sans-serif", minHeight: "calc(100vh - 60px)" }}>

          {/* PAGE BANNER */}
          <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)", padding: "40px 48px", color: "#fff", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: -50, right: -50, width: 200, height: 200, background: "linear-gradient(135deg, rgba(59,130,246,0.3) 0%, rgba(239,68,68,0.2) 100%)", borderRadius: "50%", filter: "blur(40px)" }}></div>
            <h1 style={{ margin: 0, fontSize: 32, fontWeight: 800, letterSpacing: "-0.5px", display: "flex", alignItems: "center" }}>
              <div style={{ background: "rgba(239, 68, 68, 0.2)", padding: "10px", borderRadius: "12px", marginRight: "16px" }}>
                <i className="bi bi-shield-lock-fill text-danger" style={{ fontSize: "24px" }}></i>
              </div>
              Manager Escalation Console
            </h1>
            <p style={{ margin: "12px 0 0 64px", opacity: 0.8, fontSize: 16, fontWeight: 500, maxWidth: "600px" }}>
              Monitor live worker activity across all departments and action SLA-breached permanent tickets immediately.
            </p>
          </div>

          <div className="container-fluid mt-4" style={{ maxWidth: "1400px", padding: "0 40px" }}>

            {/* TABS */}
            <ul className="nav nav-pills mb-4" style={{ borderBottom: "1px solid #e2e8f0", paddingBottom: "10px" }}>
              <li className="nav-item">
                <button className={`nav-link ${activeTab === 'live' ? 'active' : ''}`} onClick={() => setActiveTab('live')}
                  style={{ fontWeight: 700, borderRadius: "10px", padding: "10px 24px", background: activeTab === 'live' ? "#0f172a" : "transparent", color: activeTab === 'live' ? "#fff" : "#64748b" }}>
                  <i className="bi bi-activity me-2"></i> Live Floor Status
                  <span className="badge ms-2" style={{ background: activeTab === 'live' ? "rgba(255,255,255,0.2)" : "#e2e8f0", color: activeTab === 'live' ? "#fff" : "#475569" }}>{filteredTasks.length}</span>
                </button>
              </li>
              <li className="nav-item ms-3">
                <button className={`nav-link ${activeTab === 'escalations' ? 'active' : ''}`} onClick={() => setActiveTab('escalations')}
                  style={{ fontWeight: 700, borderRadius: "10px", padding: "10px 24px", background: activeTab === 'escalations' ? "#dc2626" : "transparent", color: activeTab === 'escalations' ? "#fff" : "#64748b" }}>
                  <i className="bi bi-exclamation-triangle-fill me-2"></i> Escalations
                  <span className="badge ms-2" style={{ background: activeTab === 'escalations' ? "rgba(255,255,255,0.2)" : "#fee2e2", color: activeTab === 'escalations' ? "#fff" : "#dc2626" }}>{tickets.length}</span>
                </button>
              </li>
              <li className="nav-item ms-3">
                <button className={`nav-link ${activeTab === 'manpower' ? 'active' : ''}`} onClick={() => setActiveTab('manpower')}
                  style={{ fontWeight: 700, borderRadius: "10px", padding: "10px 24px", background: activeTab === 'manpower' ? "#d97706" : "transparent", color: activeTab === 'manpower' ? "#fff" : "#64748b" }}>
                  <i className="bi bi-people-fill me-2"></i> Red Process (Manpower)
                </button>
              </li>
            </ul>

            {loading ? (
              <div className="text-center" style={{ padding: "100px 0" }}>
                <div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }}></div>
                <h5 className="mt-4 text-secondary" style={{ fontWeight: 600 }}>Syncing floor data...</h5>
              </div>
            ) : (
              <div>
                {/* TAB 1: LIVE WORKER STATUS */}
                {activeTab === 'live' && (
                  <div>
                    {/* FILTERS */}
                    <div className="card border-0 mb-4" style={{ borderRadius: "16px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)", background: "#fff" }}>
                      <div className="card-body p-4 d-flex gap-3 flex-wrap">
                        <div style={{ flex: "1 1 250px" }}>
                          <label style={{ fontSize: "12px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", marginBottom: "6px", display: "block" }}>Search Active Floor</label>
                          <div className="input-group">
                            <span className="input-group-text bg-white border-end-0"><i className="bi bi-search text-muted"></i></span>
                            <input type="text" className="form-control border-start-0 ps-0" placeholder="Lot ID, Process, Worker..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} style={{ boxShadow: "none" }} />
                          </div>
                        </div>
                        {user?.Auth === 'SuperAdmin' && (
                          <div style={{ flex: "1 1 200px" }}>
                            <label style={{ fontSize: "12px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", marginBottom: "6px", display: "block" }}>Filter by Supervisor</label>
                            <select className="form-select" value={supervisorFilter} onChange={e => setSupervisorFilter(e.target.value)} style={{ boxShadow: "none" }}>
                              <option value="">All Supervisors</option>
                              {uniqueSupervisors.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                          </div>
                        )}
                        <div style={{ flex: "1 1 200px" }}>
                          <label style={{ fontSize: "12px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", marginBottom: "6px", display: "block" }}>Filter by Worker</label>
                          <select className="form-select" value={workerFilter} onChange={e => setWorkerFilter(e.target.value)} style={{ boxShadow: "none" }}>
                            <option value="">All Workers</option>
                            {uniqueWorkers.map(w => <option key={w.id} value={w.id}>{w.name} ({w.id})</option>)}
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="card border-0" style={{ borderRadius: "20px", overflow: "hidden", background: "#fff", boxShadow: "0 10px 25px -5px rgba(0,0,0,0.05)" }}>
                      <div className="card-body p-0">
                        <div className="table-responsive">
                          <table className="table align-middle mb-0" style={{ fontSize: "15px" }}>
                            <thead style={{ background: "#1e293b" }}>
                              <tr>
                                <th style={{ color: "#fff", fontWeight: 700, padding: "16px 30px", border: "none", textTransform: "uppercase", fontSize: "12px" }}>Worker Info</th>
                                <th style={{ color: "#fff", fontWeight: 700, padding: "16px 20px", border: "none", textTransform: "uppercase", fontSize: "12px" }}>Lot / Process</th>
                                <th style={{ color: "#fff", fontWeight: 700, padding: "16px 20px", border: "none", textTransform: "uppercase", fontSize: "12px" }}>Supervisor</th>
                                <th style={{ color: "#fff", fontWeight: 700, padding: "16px 20px", border: "none", textTransform: "uppercase", fontSize: "12px" }}>Target</th>
                                <th style={{ color: "#fff", fontWeight: 700, padding: "16px 30px", border: "none", textTransform: "uppercase", fontSize: "12px" }}>Live Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredTasks.length === 0 ? (
                                <tr><td colSpan="5" className="text-center text-muted" style={{ padding: "60px 0" }}>
                                  <i className="bi bi-inbox" style={{ fontSize: "40px", color: "#cbd5e1" }}></i>
                                  <div className="mt-3" style={{ fontWeight: 600 }}>No active workers match your filters</div>
                                </td></tr>
                              ) : filteredTasks.map(t => (
                                <tr key={t.task_id} style={{ transition: "background 0.2s" }} onMouseOver={e => e.currentTarget.style.background = "#f8fafc"} onMouseOut={e => e.currentTarget.style.background = "transparent"}>
                                  <td style={{ padding: "20px 30px", borderBottom: "1px solid #e2e8f0" }}>
                                    <div style={{ fontWeight: 700, color: "#1e293b" }}>{t.First_Name} {t.Last_Name}</div>
                                    <div style={{ color: "#64748b", fontSize: "13px", marginTop: "4px" }}>ID: {t.worker_id}</div>
                                  </td>
                                  <td style={{ padding: "20px 20px", borderBottom: "1px solid #e2e8f0" }}>
                                    <div style={{ fontWeight: 800, color: "#0f172a" }}>{t.lot_id}</div>
                                    <div className="badge mt-2" style={{ background: "#e2e8f0", color: "#334155", fontWeight: 600 }}>{t.process_name}</div>
                                  </td>
                                  <td style={{ padding: "20px 20px", borderBottom: "1px solid #e2e8f0" }}>
                                    <span style={{ fontSize: "13px", fontWeight: 600, color: "#475569" }}>{t.supervisor || '-'}</span>
                                  </td>
                                  <td style={{ padding: "20px 20px", borderBottom: "1px solid #e2e8f0", fontWeight: 800, color: "#3b82f6" }}>
                                    {t.target_qty > 0 ? `${t.target_qty} pcs` : '-'}
                                  </td>
                                  <td style={{ padding: "20px 30px", borderBottom: "1px solid #e2e8f0" }}>
                                    {t.status === 'IN_PROGRESS' && (
                                      <div>
                                        <span style={{ display: "flex", alignItems: "center", background: "#ecfdf5", color: "#059669", padding: "6px 12px", borderRadius: "20px", fontWeight: 700, fontSize: "13px", width: "fit-content" }}>
                                          <span style={{ width: "8px", height: "8px", background: "#10b981", borderRadius: "50%", marginRight: "8px", boxShadow: "0 0 8px #10b981" }}></span> Running
                                        </span>
                                        <div style={{ fontSize: "12px", color: "#64748b", marginTop: "6px" }}>{getTimeElapsed(t.start_time)}</div>
                                      </div>
                                    )}
                                    {t.status === 'SLA_BREACHED' && (
                                      <span style={{ display: "flex", flexDirection: "column", alignItems: "center", background: "#fef2f2", color: "#dc2626", padding: "6px 12px", borderRadius: "8px", fontWeight: 700, fontSize: "12px", width: "fit-content", border: "1px solid #fecaca" }}>
                                        <span><i className="bi bi-exclamation-triangle-fill me-1"></i> Timeout</span>
                                        <span style={{ fontSize: "10px", opacity: 0.9 }}>Ticket Raised</span>
                                      </span>
                                    )}
                                    {t.status === 'DONE' && (
                                      <div>
                                        <span style={{ display: "flex", alignItems: "center", background: "#f0f9ff", color: "#0284c7", padding: "6px 12px", borderRadius: "20px", fontWeight: 700, fontSize: "13px", width: "fit-content" }}>
                                          <i className="bi bi-check-all me-2"></i> Completed
                                        </span>
                                        <div style={{ marginTop: "10px", fontSize: "12px", color: "#475569", background: "#f8fafc", padding: "8px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                                          <div><strong>Done:</strong> {t.completed_qty} pcs</div>
                                          {t.rejected_qty > 0 && <div className="text-danger mt-1"><strong>Rejected:</strong> {t.rejected_qty} pcs</div>}
                                          {t.hold_reason && <div className="mt-1"><strong>Note:</strong> {t.hold_reason}</div>}
                                          {t.photo_url && (
                                            <div className="mt-2">
                                              <strong style={{fontSize: '11px', display: 'block', marginBottom: '4px'}}>Completed Photo:</strong>
                                              <a href={`${IMAGE_BASE_URL}${t.photo_url}`} target="_blank" rel="noreferrer">
                                                <img src={`${IMAGE_BASE_URL}${t.photo_url}`} alt="Completed" style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', border: '2px solid #22c55e' }} />
                                              </a>
                                            </div>
                                          )}
                                          {t.rejected_photo_url && (
                                            <div className="mt-2">
                                              <strong style={{fontSize: '11px', display: 'block', marginBottom: '4px', color: '#dc2626'}}>Rejected Photo:</strong>
                                              <a href={`${IMAGE_BASE_URL}${t.rejected_photo_url}`} target="_blank" rel="noreferrer">
                                                <img src={`${IMAGE_BASE_URL}${t.rejected_photo_url}`} alt="Rejected" style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', border: '2px solid #ef4444' }} />
                                              </a>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* TAB 2: ESCALATIONS */}
                {activeTab === 'escalations' && (
                  <div className="card border-0" style={{ borderRadius: "20px", background: "linear-gradient(145deg, #ffffff, #fef2f2)", boxShadow: "0 10px 25px -5px rgba(239,68,68,0.1)" }}>
                    <div className="card-header bg-transparent border-0" style={{ padding: "30px 30px 20px" }}>
                      <h5 style={{ fontWeight: 800, color: "#991b1b", margin: 0, fontSize: "20px" }}>
                        <i className="bi bi-exclamation-triangle-fill text-danger me-2"></i> Action Required
                      </h5>
                      <p style={{ color: "#7f1d1d", margin: "6px 0 0", fontSize: "14px", fontWeight: 500, opacity: 0.8 }}>
                        Tickets that breached the 12-hour SLA. Assign a <b>new worker</b> to remediate — original worker cannot be assigned.
                      </p>
                    </div>
                    <div className="card-body p-0 px-4 pb-4">
                      {tickets.length === 0 ? (
                        <div className="text-center" style={{ padding: "80px 0" }}>
                          <div style={{ width: "80px", height: "80px", background: "#dcfce7", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto" }}>
                            <i className="bi bi-check2 text-success" style={{ fontSize: "40px" }}></i>
                          </div>
                          <h5 className="mt-4" style={{ color: "#166534", fontWeight: 800 }}>All Clear!</h5>
                          <p style={{ color: "#15803d", fontWeight: 500 }}>There are no active escalations right now.</p>
                        </div>
                      ) : (
                        <div className="row g-4">
                          {tickets.map(t => (
                            <div key={t.ticket_id} className="col-xl-4 col-lg-6 col-md-12">
                              <div className="card border-0 h-100" style={{ borderRadius: "16px", background: "#fff", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)", border: "1px solid #fca5a5" }}>
                                <div className="card-body p-4">
                                  <div className="d-flex justify-content-between align-items-center mb-3">
                                    <span className="badge" style={{ background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca", padding: "6px 12px", borderRadius: "8px", fontWeight: 800 }}>
                                      TICKET #{t.ticket_id}
                                    </span>
                                    <span style={{ fontSize: "11px", fontWeight: 800, color: "#ef4444", textTransform: "uppercase", letterSpacing: "1px", background: "#fef2f2", padding: "4px 10px", borderRadius: "20px" }}>
                                      {t.status}
                                    </span>
                                  </div>

                                  <h5 style={{ fontWeight: 800, color: "#0f172a", margin: "0 0 12px 0", fontSize: "18px" }}>
                                    <i className="bi bi-box me-2 text-muted" style={{ fontSize: "14px" }}></i>{t.lot_id}
                                  </h5>

                                  <div style={{ background: "#f8fafc", padding: "12px 16px", borderRadius: "12px", marginBottom: "16px" }}>
                                    <div className="d-flex mb-2">
                                      <span style={{ width: "70px", color: "#64748b", fontWeight: 600, fontSize: "13px" }}>Issue:</span>
                                      <span style={{ color: "#1e293b", fontWeight: 700, fontSize: "14px" }}>{t.issue_type || 'Defect'}</span>
                                    </div>
                                    <div className="d-flex mb-2">
                                      <span style={{ width: "70px", color: "#64748b", fontWeight: 600, fontSize: "13px" }}>Dept:</span>
                                      <span style={{ color: "#1e293b", fontWeight: 700, fontSize: "14px" }}>{t.department || '-'}</span>
                                    </div>
                                    <div className="d-flex mb-2">
                                      <span style={{ width: "70px", color: "#64748b", fontWeight: 600, fontSize: "13px" }}>Worker:</span>
                                      <span style={{ color: "#1e293b", fontWeight: 700, fontSize: "14px" }}>{t.original_worker_name || '-'}</span>
                                    </div>
                                    <div className="d-flex">
                                      <span style={{ width: "70px", color: "#64748b", fontWeight: 600, fontSize: "13px" }}>Raised:</span>
                                      <span style={{ color: "#475569", fontWeight: 600, fontSize: "13px" }}>{t.created_at ? new Date(t.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}</span>
                                    </div>
                                  </div>

                                  {t.status === 'REMEDIATION_ASSIGNED' ? (
                                    <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: "10px", padding: "12px 16px", textAlign: "center" }}>
                                      <i className="bi bi-person-check-fill text-success me-2"></i>
                                      <span style={{ color: "#166534", fontWeight: 700, fontSize: "14px" }}>Remediation Assigned</span>
                                    </div>
                                  ) : (
                                    <button
                                      className="btn w-100"
                                      style={{ background: "linear-gradient(135deg, #dc2626, #991b1b)", color: "#fff", fontWeight: 700, borderRadius: "10px", padding: "12px", fontSize: "14px", transition: "all 0.2s", boxShadow: "0 4px 12px rgba(220,38,38,0.3)" }}
                                      onMouseOver={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 20px rgba(220,38,38,0.4)"; }}
                                      onMouseOut={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(220,38,38,0.3)"; }}
                                      onClick={() => openRemediationModal(t)}
                                    >
                                      <i className="bi bi-person-plus-fill me-2"></i> ASSIGN REMEDIATION
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {/* TAB 3: RED PROCESS / MANPOWER */}
                {activeTab === 'manpower' && (
                  <div className="card border-0" style={{ borderRadius: "16px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)" }}>
                    <div className="card-header bg-white border-bottom-0 pt-4 pb-0 px-4">
                      <h4 style={{ fontWeight: 800, color: "#0f172a", margin: 0 }}>Red Process Adjustments</h4>
                      <p className="text-muted mt-2">Manage processes facing worker shortages by assigning overtime, borrowing workers, or halting for training.</p>
                    </div>
                    <div className="card-body p-4">
                      <div className="row g-4">
                        <div className="col-md-4">
                          <div className="p-4" style={{ background: "#f8fafc", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                            <h5 style={{ fontWeight: 700, color: "#d97706" }}><i className="bi bi-clock-history me-2"></i> Overtime (OT)</h5>
                            <p style={{ fontSize: "14px", color: "#64748b" }}>Assign existing workers approved overtime to clear the bottleneck.</p>
                            <button onClick={() => handleManpowerAction('OVERTIME')} className="btn w-100" style={{ background: "#d97706", color: "#fff", fontWeight: 600 }}>Assign OT</button>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="p-4" style={{ background: "#f8fafc", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                            <h5 style={{ fontWeight: 700, color: "#2563eb" }}><i className="bi bi-arrow-left-right me-2"></i> Borrow Worker</h5>
                            <p style={{ fontSize: "14px", color: "#64748b" }}>Temporarily reassign a trained worker from another department.</p>
                            <button onClick={() => handleManpowerAction('BORROW')} className="btn w-100" style={{ background: "#2563eb", color: "#fff", fontWeight: 600 }}>Borrow Worker</button>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="p-4" style={{ background: "#f8fafc", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                            <h5 style={{ fontWeight: 700, color: "#dc2626" }}><i className="bi bi-stop-circle-fill me-2"></i> Stop & Train</h5>
                            <p style={{ fontSize: "14px", color: "#64748b" }}>Halt the process temporarily to train additional workers.</p>
                            <button onClick={() => handleManpowerAction('TRAIN')} className="btn w-100" style={{ background: "#dc2626", color: "#fff", fontWeight: 600 }}>Log Training</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* ── REMEDIATION ASSIGN MODAL ── */}
      {showModal && selectedTicket && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.7)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px", backdropFilter: "blur(6px)" }}>
          <div style={{ background: "#fff", borderRadius: "24px", width: "100%", maxWidth: "700px", maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 25px 60px rgba(0,0,0,0.3)", animation: "slideUpModal 0.25s ease" }}>
            
            {/* Modal Header */}
            <div style={{ background: "#fff", padding: "24px 28px", borderRadius: "24px 24px 0 0", borderBottom: "2px solid #f1f5f9", flexShrink: 0 }}>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div style={{ fontSize: "12px", fontWeight: 700, color: "#ef4444", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "6px", display: "flex", alignItems: "center", gap: "6px" }}>
                    <i className="bi bi-exclamation-triangle-fill"></i> Ticket #{selectedTicket.ticket_id}
                  </div>
                  <h4 style={{ margin: 0, fontWeight: 800, fontSize: "20px", color: "#0f172a" }}>Assign Remediation Task</h4>
                  <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#64748b", fontWeight: 500 }}>
                    Lot: <b style={{ color: "#0f172a" }}>{selectedTicket.lot_id}</b> · Dept: <b style={{ color: "#0f172a" }}>{selectedTicket.department || 'N/A'}</b>
                  </p>
                </div>
                <button onClick={() => setShowModal(false)} style={{ background: "#f1f5f9", border: "none", color: "#64748b", borderRadius: "10px", width: "36px", height: "36px", cursor: "pointer", fontSize: "18px", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
              </div>
            </div>

            {/* Modal Body */}
            <div style={{ padding: "28px 32px", overflowY: "auto", flexGrow: 1 }}>
              
              {/* Info Banner */}
              <div style={{ background: "#fef9c3", border: "1px solid #fde047", borderRadius: "12px", padding: "12px 16px", marginBottom: "24px", display: "flex", gap: "10px" }}>
                <i className="bi bi-info-circle-fill text-warning mt-1"></i>
                <div style={{ fontSize: "13px", color: "#713f12", fontWeight: 600 }}>
                  Original worker <b style={{ textTransform: "uppercase", color: "#dc2626" }}>({selectedTicket?.original_worker_name || 'Unknown'})</b> <b>cannot</b> be assigned. Please choose a <b>different worker</b> for this remediation task.
                </div>
              </div>

              {/* Supervisor Search + Select */}
              <div className="mb-4">
                <label style={{ fontWeight: 700, fontSize: "13px", color: "#374151", marginBottom: "8px", display: "block", textTransform: "uppercase" }}>
                  <i className="bi bi-shield-check me-2 text-indigo"></i> Responsible Supervisor *
                  <span style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", marginLeft: "8px", textTransform: "none" }}>
                    (For the generated Recovery Ticket)
                  </span>
                </label>
                <Select
                  options={supervisorsList.map(s => ({ value: s.supervisor_name, label: s.supervisor_name }))}
                  value={assignSupervisor ? { value: assignSupervisor, label: assignSupervisor } : null}
                  onChange={(selectedOption) => setAssignSupervisor(selectedOption ? selectedOption.value : '')}
                  isClearable
                  placeholder="Select Responsible Supervisor..."
                  isLoading={supervisorsList.length === 0}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderRadius: "12px",
                      padding: "4px 8px",
                      borderColor: state.isFocused ? "#4f46e5" : "#e2e8f0",
                      boxShadow: state.isFocused ? "0 0 0 3px rgba(79, 70, 229, 0.1)" : "none",
                      fontWeight: 600,
                      "&:hover": { borderColor: "#cbd5e1" }
                    }),
                    menu: (base) => ({
                      ...base,
                      borderRadius: "12px",
                      boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)",
                      overflow: "hidden"
                    }),
                    option: (base, state) => ({
                      ...base,
                      fontWeight: 600,
                      backgroundColor: state.isSelected ? "#4f46e5" : state.isFocused ? "#e0e7ff" : "white",
                      color: state.isSelected ? "white" : "#1e293b",
                      cursor: "pointer",
                      padding: "10px 16px",
                      "&:active": { backgroundColor: "#c7d2fe" }
                    })
                  }}
                />
              </div>

              {/* Worker Search + Select */}
              <div className="mb-4">
                <label style={{ fontWeight: 700, fontSize: "13px", color: "#374151", marginBottom: "8px", display: "block", textTransform: "uppercase" }}>
                  <i className="bi bi-person-fill me-2 text-primary"></i> Select Worker *
                  <span style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", marginLeft: "8px", textTransform: "none" }}>
                    ({user?.Department} dept — {workersList.filter(w => w.worker_name !== selectedTicket?.original_worker_name).length} eligible workers)
                  </span>
                </label>
                <Select
                  options={workersList.filter(w => w.worker_name !== selectedTicket?.original_worker_name).map(w => ({ value: w.worker_id, label: w.worker_name }))}
                  value={assignWorker ? { value: assignWorker, label: workersList.find(w => w.worker_id === assignWorker)?.worker_name } : null}
                  onChange={(selectedOption) => setAssignWorker(selectedOption ? selectedOption.value : '')}
                  isClearable
                  placeholder="Search eligible worker by name..."
                  isLoading={workersList.length === 0}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderRadius: "12px",
                      padding: "4px 8px",
                      borderColor: state.isFocused ? "#3b82f6" : "#e2e8f0",
                      boxShadow: state.isFocused ? "0 0 0 3px rgba(59, 130, 246, 0.1)" : "none",
                      fontWeight: 600,
                      "&:hover": { borderColor: "#cbd5e1" }
                    }),
                    menu: (base) => ({
                      ...base,
                      borderRadius: "12px",
                      overflow: "hidden",
                      boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
                      zIndex: 100
                    }),
                    option: (base, state) => ({
                      ...base,
                      padding: "10px 14px",
                      fontWeight: 600,
                      backgroundColor: state.isSelected ? "#eff6ff" : state.isFocused ? "#f8fafc" : "#fff",
                      color: state.isSelected ? "#1d4ed8" : "#1e293b",
                      borderLeft: state.isSelected ? "3px solid #3b82f6" : "3px solid transparent",
                      cursor: "pointer"
                    })
                  }}
                />
              </div>

              {/* Qty Input with max limit */}
              <div className="mb-4">
                <label style={{ fontWeight: 700, fontSize: "13px", color: "#374151", marginBottom: "8px", display: "flex", justifyContent: "space-between", alignItems: "center", textTransform: "uppercase" }}>
                  <span><i className="bi bi-boxes me-2 text-success"></i> Quantity to Remediate (pcs) *</span>
                  <span style={{ background: "#fef2f2", color: "#dc2626", padding: "2px 10px", borderRadius: "20px", fontSize: "12px", fontWeight: 700, textTransform: "none" }}>
                    Max: {availableQty} pcs (shortage)
                  </span>
                </label>
                <input
                  type="number"
                  className="form-control"
                  value={assignQty}
                  onChange={e => {
                    const val = parseInt(e.target.value);
                    if (val > availableQty) setAssignQty(String(availableQty));
                    else setAssignQty(e.target.value);
                  }}
                  placeholder={`Max ${availableQty} pcs`}
                  min="1"
                  max={availableQty}
                  style={{ borderRadius: "12px", padding: "12px 16px", border: `2px solid ${assignQty && parseInt(assignQty) > availableQty ? '#ef4444' : '#e2e8f0'}`, fontWeight: 600, boxShadow: "none" }}
                />
                {assignQty && parseInt(assignQty) > availableQty && (
                  <div style={{ color: "#ef4444", fontSize: "12px", fontWeight: 600, marginTop: "6px" }}>
                    ⚠️ Shortage sirf {availableQty} pcs hai! Isse zyada assign nahi kar sakte.
                  </div>
                )}
              </div>

              {/* Standard Time & Due Time */}
              <div className="row mb-4">
                <div className="col-md-6">
                  <label style={{ fontWeight: 700, fontSize: "13px", color: "#374151", marginBottom: "8px", display: "block", textTransform: "uppercase" }}>
                    <i className="bi bi-clock me-2 text-warning"></i> Standard Time (mins)
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={standardTime}
                    onChange={e => setStandardTime(e.target.value)}
                    placeholder="e.g. 120"
                    min="1"
                    style={{ borderRadius: "12px", padding: "12px 16px", border: "2px solid #e2e8f0", fontWeight: 600, boxShadow: "none" }}
                  />
                </div>
                <div className="col-md-6">
                  <label style={{ fontWeight: 700, fontSize: "13px", color: "#374151", marginBottom: "8px", display: "block", textTransform: "uppercase" }}>
                    <i className="bi bi-calendar-event me-2 text-danger"></i> Due Time
                  </label>
                  <input
                    type="datetime-local"
                    className="form-control"
                    value={dueTime}
                    onChange={e => setDueTime(e.target.value)}
                    style={{ borderRadius: "12px", padding: "12px 16px", border: "2px solid #e2e8f0", fontWeight: 600, boxShadow: "none" }}
                  />
                </div>
              </div>

              {/* Verifier Selection */}
              <div className="mb-4">
                <label style={{ fontWeight: 700, fontSize: "13px", color: "#374151", marginBottom: "8px", display: "block", textTransform: "uppercase" }}>
                  <i className="bi bi-shield-check me-2 text-primary"></i> Verifier Selection
                </label>
                <select
                  className="form-select"
                  value={verifier}
                  onChange={e => setVerifier(e.target.value)}
                  style={{ borderRadius: "12px", padding: "12px 16px", border: "2px solid #e2e8f0", fontWeight: 600, boxShadow: "none", cursor: "pointer" }}
                >
                  <option value="QC">QC</option>
                  <option value="Final Checker">Final Checker</option>
                  <option value="Manager">Manager</option>
                  <option value="Original Complainant">Original Complainant</option>
                </select>
              </div>

              {/* Notes */}
              <div className="mb-4">
                <label style={{ fontWeight: 700, fontSize: "13px", color: "#374151", marginBottom: "8px", display: "block", textTransform: "uppercase" }}>
                  <i className="bi bi-chat-text me-2 text-muted"></i> Notes (Optional)
                </label>
                <textarea
                  className="form-control"
                  value={assignNotes}
                  onChange={e => setAssignNotes(e.target.value)}
                  rows={3}
                  placeholder="Additional instructions for the worker..."
                  style={{ borderRadius: "12px", padding: "12px 16px", border: "2px solid #e2e8f0", fontWeight: 500, boxShadow: "none", resize: "none" }}
                />
              </div>

              {/* Action Buttons */}
              <div className="d-flex gap-3">
                <button
                  className="btn flex-grow-1"
                  onClick={() => setShowModal(false)}
                  style={{ background: "#f1f5f9", color: "#475569", borderRadius: "12px", padding: "14px", fontWeight: 700, border: "none" }}
                >
                  Cancel
                </button>
                <button
                  className="btn flex-grow-1"
                  onClick={handleAssignRemediation}
                  disabled={assigning}
                  style={{ background: "linear-gradient(135deg, #dc2626, #991b1b)", color: "#fff", borderRadius: "12px", padding: "14px", fontWeight: 800, border: "none", boxShadow: "0 4px 12px rgba(220,38,38,0.3)", transition: "all 0.2s" }}
                >
                  {assigning ? (
                    <><span className="spinner-border spinner-border-sm me-2"></span> Assigning...</>
                  ) : (
                    <><i className="bi bi-person-check-fill me-2"></i> Assign Task</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideUpModal {
          from { opacity: 0; transform: translateY(30px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .form-select:focus, .form-control:focus {
          border-color: #3b82f6 !important;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
        }
      `}</style>
    </div>
  );
}
